import { Component, Input, Output, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { CustomerService } from '../../../../../../../../../../services/customer/customer.service';
import { objHasNonEmptyValues, propExists } from '../../../../../../../../../../utils';
import { OrganizationConfigService } from '../../../../../../../../../../services/organization-config/organization-config.service';
import {
  CustomerMembership,
  CustomerMembershipCustomer,
  MembershipPerCustomerAddOn
} from '../../../../../../../../../../models/membership';

@Component({
  selector: 'app-edit-recurring-customer-membership-add-ons-next-bill-date',
  templateUrl: './edit-recurring-customer-membership-add-ons-next-bill-date.component.html',
  styleUrls: ['./edit-recurring-customer-membership-add-ons-next-bill-date.component.css']
})
export class EditRecurringCustomerMembershipAddOnsNextBillDateComponent implements OnInit, OnDestroy {

  @Input() membership: CustomerMembership;
  @Input() savedCallback: (successMessage: string) => undefined;
  @Input() saveClickedObservable: Observable<void>;
  @Output() loadingEvent = new EventEmitter<boolean>();
  @Output() serverErrorEvent = new EventEmitter<number>();

  customerAddOns: {[key: string]: MembershipPerCustomerAddOn[]} = {};

  public errorMessage = '';

  private saveClickedSubscription: Subscription;

  constructor(
    public orgConfig: OrganizationConfigService,
    private activeModal: NgbActiveModal,
    private customerService: CustomerService
  ) {}

  ngOnInit() {
    this.saveClickedSubscription = this.saveClickedObservable.subscribe(() => {
      this.handleSave();
    });

    // Create a deep copy of the customer add-ons, to avoid editing the
    // membership directly (this would cause the membership to be updated in
    // the list before it is changed on the server).
    for (const customer of this.membership.all_customers) {
      const customerIdStr = customer.id.toString();
      this.customerAddOns[customerIdStr] = [];

      if (propExists(this.membership.customer_add_ons[customerIdStr])) {
        for (const addOn of this.membership.customer_add_ons[customerIdStr]) {
          this.customerAddOns[customerIdStr].push(addOn);
        }
      }
    }
  }

  ngOnDestroy() {
    this.saveClickedSubscription.unsubscribe();
  }

  public addOnIsSelectedForCustomer(customer: CustomerMembershipCustomer,
                                    addOn: MembershipPerCustomerAddOn) {
    let thisCustomerAddOns = this.customerAddOns[customer.id.toString()];

    if (!propExists(thisCustomerAddOns)) {
      this.customerAddOns[customer.id.toString()] = [];
      thisCustomerAddOns = this.customerAddOns[customer.id.toString()];
    }

    for (const thisAddOn of thisCustomerAddOns) {
      if (thisAddOn.id === addOn.id) {
        return true;
      }
    }

    return false;
  }

  public toggleAddOnForCustomer(customer: CustomerMembershipCustomer,
                                addOn: MembershipPerCustomerAddOn) {
    const thisCustomerAddOns = this.customerAddOns[customer.id.toString()];

    for (let i = 0; i < thisCustomerAddOns.length; i++) {
      const thisAddOn = thisCustomerAddOns[i];

      // If the add-on is found in the list, remove it and return.
      if (thisAddOn.id === addOn.id) {
        thisCustomerAddOns.splice(i, 1);
        return;
      }
    }

    // If we get to this line, then the add on was not found in the list, so
    // add it.
    thisCustomerAddOns.push(addOn);
  }

  public membershipHasPendingAddOnChanges(): boolean {
    return (
      objHasNonEmptyValues(this.membership.customer_add_ons_to_add) ||
      objHasNonEmptyValues(this.membership.customer_add_ons_to_remove)
    );
  }

  private handleSave() {
    this.loadingEvent.emit(true);

    this.customerService
      .editMembershipCustomerAddOnsNextBillDate(this.membership.id, this.customerAddOns)
      .then(() => {
        this.savedCallback('Membership add-ons edited successfully.');
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to edit this membership\'s add-ons.';
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverErrorEvent.emit(reason.status);
        }
      })
      .finally(() => { this.loadingEvent.emit(false); });
  }

}
